import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import searchNewIcon from "../assets/searchNew.svg";
import statisticsIcon from "../assets/statisticsIcon.svg";
import reportIcon from "../assets/reportBlack.svg";
import calenderIcon from "../assets/calenderIcon.svg";
import refreshIcon from "../assets/refreshIcon.svg";
import ChevronDown from "../assets/chevron-down.svg";
import ReportView, {
  buildCcDoctorProcessedStageCountParams,
} from "./ReportView";
import StatisticsView, { DOCTOR_STATS_PAGE_LIMIT } from "./StatisticsView";
import PerformanceChart from "./PerformanceChart";
import { ReportServices } from "../services/ReportServices";
import { InterUserMangementServices } from "../services/InternalUserMangementServices";
import {
  getChartDateRangeForTimeframe,
  getChartDateRangeForYmdStrings,
  localYmdFromOffsetISO,
} from "./doctorReportDateRange";
import {
  buildPerformanceChartSeries,
  isIntegerHourSlotKey,
  PERFORMANCE_CHART_EXPANDED_VALUE_DOT_PX,
  PERFORMANCE_CHART_SMALL_DOT_HEIGHT_PX,
  PERFORMANCE_CHART_SMALL_DOT_RADIUS_PX,
  PERFORMANCE_CHART_SMALL_DOT_WIDTH_PX,
} from "./performanceChartData";
import { formatExpandedHourRangeLabel } from "./performanceChartData";
import { formatExpandedHourStartIST } from "./performanceChartData";
import DoctorReportDownloadButton from "./DoctorReportDownloadButton";

const CC_DOCTOR_PAGE_LIMIT = 10;
/** Idle time after typing before auto-scrolling to the report table (Enter scrolls immediately). */
const CASE_SEARCH_SCROLL_IDLE_MS = 2000;

/** Short label for the timeframe control when using a custom date range. */
function formatYmdRangeLabel(startYmd, endYmd) {
  if (!startYmd || !endYmd) return "Custom range";
  const p = (s) => s.split("-").map(Number);
  const [y1, m1, d1] = p(startYmd);
  const [y2, m2, d2] = p(endYmd);
  if (![y1, m1, d1, y2, m2, d2].every(Number.isFinite)) return "Custom range";
  const a = new Date(y1, m1 - 1, d1);
  const b = new Date(y2, m2 - 1, d2);
  const optsShort = { month: "short", day: "numeric" };
  const optsFull = { month: "short", day: "numeric", year: "numeric" };
  const sa = a.toLocaleDateString(undefined, y1 === y2 ? optsShort : optsFull);
  const sb = b.toLocaleDateString(undefined, optsFull);
  return `${sa} – ${sb}`;
}

function ccDoctorDisplayName(u) {
  if (!u) return "—";
  const fn = u.fullName != null ? String(u.fullName).trim() : "";
  if (fn) return fn;
  const un = u.userName != null ? String(u.userName).trim() : "";
  if (un) return un;
  const em = u.email != null ? String(u.email).trim() : "";
  return em || "—";
}

function mergeTeamUsersById(existing, incoming) {
  const seen = new Set(existing.map((x) => x.id));
  const out = [...existing];
  for (const u of incoming) {
    if (u?.id && !seen.has(u.id)) {
      seen.add(u.id);
      out.push(u);
    }
  }
  return out;
}

/** GET cc-doctor-processed-stage-count body keys → lowercase numeric map. */
function normalizeProcessedStageCountPayload(data) {
  if (!data || typeof data !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(data)) {
    const key = String(k).toLowerCase();
    const n = Number(v);
    out[key] = Number.isFinite(n) ? n : 0;
  }
  return out;
}

function mapProcessedStageCounts(normalized) {
  const d = normalized;
  const daycare = d.daycare ?? 0;
  const conservative = d.conservative ?? 0;
  const surgical = d.surgical ?? 0;
  const noCaseType = d.nocasetype ?? 0;
  const admission = d.admission ?? 0;
  const enhancement = d.enhancement ?? 0;
  const dischargeclaim = d.dischargeclaim ?? 0;

  return {
    caseType: {
      All: daycare + conservative + surgical + noCaseType,
      Daycare: daycare,
      Conservative: conservative,
      Surgical: surgical,
    },
    caseStage: {
      All: admission + enhancement + dischargeclaim,
      Admission: admission,
      Enhancement: enhancement,
      Claim: dischargeclaim,
    },
  };
}

function formatHeaderFilterCount(n) {
  if (!Number.isFinite(n)) return "";
  try {
    return n.toLocaleString("en-IN");
  } catch {
    return String(n);
  }
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

const CHART_LABELS = [
  "0",
  "150",
  "260",
  "275",
  "150",
  "120",
  "40",
  "120",
  "130",
  "150",
];
const CHART_DATA = [-80, -60, 80, 180, 220, 100, 60, -60, -100, -80, -50];

const createChartGradient = (ctx, chart) => {
  const height = chart?.height ?? 1;
  if (!height || !Number.isFinite(height)) return "#EDFF8C";
  const yScale = chart?.scales?.y;
  if (!yScale) return "#EDFF8C";
  const yZero = yScale.getPixelForValue(0);
  if (!Number.isFinite(yZero)) return "#EDFF8C";
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  const ratio = Math.max(0.02, Math.min(0.98, yZero / height));
  const clamp = (v) => Math.max(0, Math.min(1, v));
  gradient.addColorStop(0, "#EDFF8C");
  gradient.addColorStop(clamp(ratio - 0.005), "#EDFF8C");
  gradient.addColorStop(clamp(ratio), "rgba(255, 67, 67, 0)");
  gradient.addColorStop(
    clamp(ratio + (1 - ratio) * 0.0661),
    "rgba(255, 67, 67, 0)",
  );
  gradient.addColorStop(clamp(ratio + (1 - ratio) * 0.8932), "#FF4343");
  gradient.addColorStop(1, "#FF4343");
  return gradient;
};

const getChartConfig = (data) => ({
  labels: CHART_LABELS,
  datasets: [
    {
      label: "Performance",
      data,
      fill: true,
      backgroundColor: (context) => {
        const chart = context.chart;
        const meta = chart.getDatasetMeta(0);
        if (!meta.data.length) return "transparent";
        return createChartGradient(chart.ctx, chart);
      },
      borderColor: "transparent",
      borderWidth: 0,
      pointBackgroundColor: "#000000",
      pointBorderColor: "#000000",
      pointRadius: PERFORMANCE_CHART_SMALL_DOT_RADIUS_PX,
      pointBorderWidth: 0,
      tension: 0.3,
    },
  ],
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: true },
    datalabels: { display: false },
  },
  scales: {
    x: {
      grid: { display: true, color: "rgba(0,0,0,0.06)" },
      border: { display: false },
      ticks: { font: { size: 11 }, maxRotation: 0, color: "#333" },
    },
    y: {
      grid: { display: false },
      border: { display: false },
      ticks: { display: false },
      position: "left",
      min: -100,
      max: 300,
      suggestedMin: -100,
      suggestedMax: 300,
    },
  },
};

// Outer track: 420×80, border-radius 100px. All content inside border with clear padding.
// const EXPANDED_BAR_HEIGHT = 420;
// const EXPANDED_BAR_WIDTH = 80;
// const EXPANDED_OUTER_PADDING = { top: 12, right: 30, bottom: 12, left: 30 };
// const EXPANDED_FILL_INSET = 8; // inset so fill never touches border edges

/**
 * Same series as PerformanceChart: each bar uses `point.y` for integer-hour slots only (no half-hour keys).
 * Bar height = `y - halfHourStages` — identical deviation from the dashed baseline as the main line at that dot.
 */
// function ExpandedChart({ payload = null }) {
//   const series = useMemo(() => {
//     const fr = payload?.formattedReport;
//     if (!fr || typeof fr !== "object") {
//       return buildPerformanceChartSeries({});
//     }
//     return buildPerformanceChartSeries(fr);
//   }, [payload]);

//   const baselineY = Number.isFinite(Number(payload?.halfHourStages))
//     ? Number(payload.halfHourStages)
//     : 0;

//   const { bars, maxPositive, maxNegative } = useMemo(() => {
//     const { points } = series;
//     if (!points?.length) {
//       return { bars: [], maxPositive: 1, maxNegative: 1 };
//     }
//     const b = Number.isFinite(baselineY) ? baselineY : 0;
//     const bars = [];
//     for (const p of points) {
//       if (!isIntegerHourSlotKey(p.key)) continue;
//       const y = Number(p.y);
//       if (!Number.isFinite(y)) continue;
//       const delta = y - b;
//       if (Math.abs(delta) < 1e-9) continue;
//       bars.push({ key: String(p.key), delta });
//     }
//     let mp = 0;
//     let mn = 0;
//     bars.forEach(({ delta }) => {
//       if (delta > mp) mp = delta;
//       if (delta < 0 && -delta > mn) mn = -delta;
//     });
//     return {
//       bars,
//       maxPositive: mp > 0 ? mp : 1,
//       maxNegative: mn > 0 ? mn : 1,
//     };
//   }, [series, baselineY]);

//   const showPlaceholderBars = bars.length === 0;

//   return (
//     <div className="w-full flex flex-col gap-2">
//       <div className="w-full flex items-stretch gap-4 lg:gap-6">
//         {/* Y-axis labels */}
//         <div
//           className="flex flex-col justify-between items-center shrink-0 py-1"
//           style={{ height: EXPANDED_BAR_HEIGHT }}
//         >
//           <span className="font-lufga font-normal text-sm text-gray-700">
//             +
//           </span>
//           <span className="font-lufga font-normal text-sm text-gray-700">
//             0
//           </span>
//           <span className="font-lufga font-normal text-sm text-gray-700">
//             −
//           </span>
//         </div>

//         {/* Distribute bars across full width with even spacing; scroll if many hours. */}
//         <div
//           className={`flex-1 w-full flex items-center min-w-0 overflow-x-auto pb-1 [scrollbar-gutter:stable] ${
//             showPlaceholderBars || bars.length <= 1
//               ? "justify-center"
//               : "justify-evenly"
//           }`}
//         >
//           {showPlaceholderBars
//             ? EXPANDED_PLACEHOLDER_LABELS.map((label) => (
//                 <ExpandedBarSkeleton key={label} label={label} />
//               ))
//             : bars.map(({ key, delta }) => (
//                 <ExpandedBar
//                   key={key}
//                   value={delta}
//                   maxPositive={maxPositive}
//                   maxNegative={maxNegative}
//                 />
//               ))}
//         </div>
//       </div>
//       {showPlaceholderBars ? (
//         <p className="text-center text-xs font-lufga text-gray-500 leading-snug px-2">
//           Preview — hourly variance vs half-hour target appears as vertical
//           tracks when data is available
//         </p>
//       ) : null}
//     </div>
//   );
// }

// const EXPANDED_PLACEHOLDER_LABELS = [
//   "06–07",
//   "10–11",
//   "14–15",
//   "18–19",
//   "22–23",
// ];

// function ExpandedBarSkeleton({ label }) {
//   const barHeight = EXPANDED_BAR_HEIGHT;
//   const barWidth = EXPANDED_BAR_WIDTH;
//   const pad = EXPANDED_OUTER_PADDING;
//   const contentHeight = barHeight - pad.top - pad.bottom;
//   const centerY = pad.top + contentHeight / 2;
//   const dotPositions = [0, 0.25, 0.5, 0.75, 1];

//   return (
//     <div
//       className="flex flex-col items-center shrink-0 gap-1.5"
//       style={{ width: barWidth }}
//     >
//       <div
//         className="relative overflow-hidden bg-white"
//         style={{
//           width: barWidth,
//           height: barHeight,
//           paddingTop: pad.top,
//           paddingRight: pad.right,
//           paddingBottom: pad.bottom,
//           paddingLeft: pad.left,
//           border: "1px solid rgba(0, 0, 0, 0.14)",
//           borderRadius: 100,
//           boxSizing: "border-box",
//         }}
//         aria-hidden
//       >
//         {dotPositions.map((frac) => (
//           <div
//             key={frac}
//             className="absolute left-1/2 rounded-full bg-gray-300/90 -translate-x-1/2 -translate-y-1/2"
//             style={{
//               left: "50%",
//               top: `${pad.top + frac * contentHeight}px`,
//               width: PERFORMANCE_CHART_SMALL_DOT_WIDTH_PX,
//               height: PERFORMANCE_CHART_SMALL_DOT_HEIGHT_PX,
//             }}
//           />
//         ))}
//         <div
//           className="absolute left-2 right-2 pointer-events-none"
//           style={{
//             top: centerY,
//             borderTop: "1px dashed rgba(0, 0, 0, 0.12)",
//           }}
//         />
//         <div
//           className="absolute left-1/2 rounded-full bg-gray-200 -translate-x-1/2 -translate-y-1/2 z-10 border border-gray-300/80"
//           style={{
//             top: centerY,
//             width: PERFORMANCE_CHART_EXPANDED_VALUE_DOT_PX,
//             height: PERFORMANCE_CHART_EXPANDED_VALUE_DOT_PX,
//           }}
//         />
//       </div>
//       <span className="font-lufga text-[10px] leading-tight text-gray-400 text-center max-w-[72px]">
//         {label}
//       </span>
//     </div>
//   );
// }

// function ExpandedBar({ value, maxPositive, maxNegative }) {
//   const barHeight = EXPANDED_BAR_HEIGHT;
//   const barWidth = EXPANDED_BAR_WIDTH;
//   const pad = EXPANDED_OUTER_PADDING;
//   const contentHeight = barHeight - pad.top - pad.bottom;
//   const centerY = pad.top + contentHeight / 2;

//   // Max fill height on each side of center, inset from edges so fill never touches border
//   const maxHalfFill = contentHeight / 2 - EXPANDED_FILL_INSET;

//   const isPositive = value >= 0;
//   const fillRatio = isPositive
//     ? Math.min(1, value / maxPositive)
//     : Math.min(1, Math.abs(value) / maxNegative);
//   const fillHeight = maxHalfFill * fillRatio;

//   const innerBarWidth = barWidth - pad.left - pad.right;
//   const fillWidth = Math.min(24, Math.max(16, innerBarWidth - 20));

//   const dotPosition = isPositive ? centerY - fillHeight : centerY + fillHeight;

//   // 5 small reference dots, evenly spaced within content area (fully inside border)
//   const dotPositions = [0, 0.25, 0.5, 0.75, 1];

//   return (
//     <div
//       className="flex flex-col items-center shrink-0"
//       style={{ width: barWidth }}
//     >
//       <div
//         className="relative overflow-hidden"
//         style={{
//           width: barWidth,
//           height: barHeight,
//           paddingTop: pad.top,
//           paddingRight: pad.right,
//           paddingBottom: pad.bottom,
//           paddingLeft: pad.left,
//           border: "1px solid rgba(0, 0, 0, 1)",
//           borderRadius: 100,
//           backgroundColor: "#fff",
//           boxSizing: "border-box",
//         }}
//       >
//         {/* 5 small reference dots along vertical center - all inside border */}
//         {dotPositions.map((frac) => (
//           <div
//             key={frac}
//             className="absolute left-1/2 rounded-full bg-black -translate-x-1/2 -translate-y-1/2"
//             style={{
//               left: "50%",
//               top: `${pad.top + frac * contentHeight}px`,
//               width: PERFORMANCE_CHART_SMALL_DOT_WIDTH_PX,
//               height: PERFORMANCE_CHART_SMALL_DOT_HEIGHT_PX,
//             }}
//           />
//         ))}

//         {/* Colored fill - inset so it never touches border */}
//         <div
//           className="absolute left-1/2 -translate-x-1/2 rounded-full"
//           style={{
//             width: fillWidth,
//             height: fillHeight,
//             borderRadius: 100,
//             backgroundColor: isPositive
//               ? "rgba(237, 255, 140, 0.8)"
//               : "rgba(255, 67, 67, 0.6)",
//             ...(isPositive
//               ? {
//                   top: centerY - fillHeight,
//                   borderTopLeftRadius: 100,
//                   borderTopRightRadius: 100,
//                 }
//               : {
//                   top: centerY,
//                   borderBottomLeftRadius: 100,
//                   borderBottomRightRadius: 100,
//                 }),
//           }}
//         />

//         {/* Value indicator - large black circle (Figma 24×24) */}
//         <div
//           className="absolute left-1/2 rounded-full bg-black -translate-x-1/2 -translate-y-1/2 z-10"
//           style={{
//             top: dotPosition,
//             width: PERFORMANCE_CHART_EXPANDED_VALUE_DOT_PX,
//             height: PERFORMANCE_CHART_EXPANDED_VALUE_DOT_PX,
//           }}
//         />
//       </div>
//     </div>
//   );
// }

//Code changes for the bar width start

const EXPANDED_BAR_WIDTH_MIN = 24; // smallest pill width (mobile ~320px screen)
const EXPANDED_BAR_WIDTH_MAX = 120; // largest pill width (wide desktop ~1280px screen)

const EXPANDED_BAR_HEIGHT = 420;
const EXPANDED_OUTER_PADDING = { top: 12, right: 30, bottom: 12, left: 30 };
const EXPANDED_FILL_INSET = 8;

function getBarPadding(barWidth) {
  const side = Math.max(6, Math.floor(barWidth * 0.18));
  return { top: 12, bottom: 12, left: side, right: side };
}

function useBarWidth(barCount, containerRef) {
  const [sizing, setSizing] = React.useState({
    barWidth: EXPANDED_BAR_WIDTH_MAX,
    gap: 12,
  });

  React.useEffect(() => {
    function calculate() {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const count = barCount > 0 ? barCount : 1;

      const GAP_MIN = 4;
      const GAP_MAX = 32;

      let gap = GAP_MAX;
      let computed = Math.floor((containerWidth - gap * (count - 1)) / count);

      if (computed > EXPANDED_BAR_WIDTH_MAX) {
        gap = Math.floor(
          (containerWidth - EXPANDED_BAR_WIDTH_MAX * count) / (count - 1 || 1),
        );
        gap = Math.min(gap, 80);
        computed = EXPANDED_BAR_WIDTH_MAX;
      }

      if (computed < EXPANDED_BAR_WIDTH_MIN) {
        gap = GAP_MIN;
        computed = Math.floor((containerWidth - GAP_MIN * (count - 1)) / count);
      }

      const clampedWidth = Math.min(
        EXPANDED_BAR_WIDTH_MAX,
        Math.max(EXPANDED_BAR_WIDTH_MIN, computed),
      );
      const clampedGap = Math.max(GAP_MIN, gap);

      setSizing({ barWidth: clampedWidth, gap: clampedGap });
    }

    calculate();
    const observer = new ResizeObserver(calculate);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [barCount, containerRef]);

  return sizing;
}

function ExpandedChart({ payload = null }) {
  const containerRef = React.useRef(null);
  const series = useMemo(() => {
    const fr = payload?.formattedReport;
    if (!fr || typeof fr !== "object") {
      return buildPerformanceChartSeries({});
    }
    return buildPerformanceChartSeries(fr);
  }, [payload]);

  const baselineY = Number.isFinite(Number(payload?.halfHourStages))
    ? Number(payload.halfHourStages)
    : 0;

  const { bars, maxPositive, maxNegative } = useMemo(() => {
    const { points } = series;
    if (!points?.length) {
      return { bars: [], maxPositive: 1, maxNegative: 1 };
    }
    const b = Number.isFinite(baselineY) ? baselineY : 0;
    const bars = [];

    for (let i = 0; i < points.length; i++) {
      const p = points[i];

      if (!isIntegerHourSlotKey(p.key)) continue;

      const firstHalf = Number(p.y) || 0;
      const next = points[i + 1];

      let secondHalf = 0;

      if (next && Number(next.key) === Number(p.key) + 0.5) {
        secondHalf = Number(next.y) || 0;
      }

      const hourlyValue = firstHalf + secondHalf;

      const delta = hourlyValue - baselineY * 2;

      bars.push({
        key: String(p.key),
        delta,
        hourIndex: Number(p.key),
      });
    }
    let mp = 0;
    let mn = 0;
    bars.forEach(({ delta }) => {
      if (delta > mp) mp = delta;
      if (delta < 0 && -delta > mn) mn = -delta;
    });
    return {
      bars,
      maxPositive: mp > 0 ? mp : 1,
      maxNegative: mn > 0 ? mn : 1,
    };
  }, [series, baselineY]);

  const showPlaceholderBars = bars.length === 0;

  const barCount = showPlaceholderBars
    ? EXPANDED_PLACEHOLDER_LABELS.length
    : bars.length;
  const { barWidth, gap } = useBarWidth(barCount, containerRef);
  return (
    <div className="w-full flex flex-col gap-2">
      <div className="w-full flex items-stretch gap-4 lg:gap-6">
        <div
          className="flex flex-col justify-between items-center shrink-0 py-1"
          style={{ height: EXPANDED_BAR_HEIGHT }}
        >
          <span className="font-lufga font-normal text-sm text-gray-700">
            +
          </span>
          <span className="font-lufga font-normal text-sm text-gray-700">
            0
          </span>
          <span className="font-lufga font-normal text-sm text-gray-700">
            −
          </span>
        </div>
        <div
          ref={containerRef}
          className="flex-1 w-full flex items-center min-w-0 flex-wrap"
          style={{ gap }}
        >
          {showPlaceholderBars
            ? EXPANDED_PLACEHOLDER_LABELS.map((label) => (
                <ExpandedBarSkeleton
                  key={label}
                  label={label}
                  barWidth={barWidth}
                />
              ))
            : bars.map(({ key, delta }) => (
                <ExpandedBar
                  key={key}
                  value={delta}
                  hourIndex={Number(key)}
                  maxPositive={maxPositive}
                  maxNegative={maxNegative}
                  barWidth={barWidth}
                />
              ))}
        </div>
      </div>

      {showPlaceholderBars ? (
        <p className="text-center text-xs font-lufga text-gray-500 leading-snug px-2">
          Preview — hourly variance vs half-hour target appears as vertical
          tracks when data is available
        </p>
      ) : null}
    </div>
  );
}
const EXPANDED_PLACEHOLDER_LABELS = [
  "06–07",
  "10–11",
  "14–15",
  "18–19",
  "22–23",
];

function ExpandedBarSkeleton({ label, barWidth }) {
  // REMOVED: const barWidth = EXPANDED_BAR_WIDTH;
  const barHeight = EXPANDED_BAR_HEIGHT;
  const pad = EXPANDED_OUTER_PADDING;
  const contentHeight = barHeight - pad.top - pad.bottom;
  const centerY = pad.top + contentHeight / 2;
  const dotPositions = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div
      className="flex flex-col items-center shrink-0 gap-1.5"
      style={{ width: barWidth }} // CHANGED: now uses responsive barWidth prop
    >
      <div
        className="relative overflow-hidden bg-white"
        style={{
          width: barWidth, // CHANGED: now uses responsive barWidth prop
          height: barHeight,
          paddingTop: pad.top,
          paddingRight: pad.right,
          paddingBottom: pad.bottom,
          paddingLeft: pad.left,
          border: "1px solid rgba(0, 0, 0, 0.14)",
          borderRadius: 100,
          boxSizing: "border-box",
        }}
        aria-hidden
      >
        {dotPositions.map((frac) => (
          <div
            key={frac}
            className="absolute left-1/2 rounded-full bg-gray-300/90 -translate-x-1/2 -translate-y-1/2"
            style={{
              left: "50%",
              top: `${pad.top + frac * contentHeight}px`,
              width: PERFORMANCE_CHART_SMALL_DOT_WIDTH_PX,
              height: PERFORMANCE_CHART_SMALL_DOT_HEIGHT_PX,
            }}
          />
        ))}
        <div
          className="absolute left-2 right-2 pointer-events-none"
          style={{
            top: centerY,
            borderTop: "1px dashed rgba(0, 0, 0, 0.12)",
          }}
        />
        <div
          className="absolute left-1/2 rounded-full bg-gray-200 -translate-x-1/2 -translate-y-1/2 z-10 border border-gray-300/80"
          style={{
            top: centerY,
            width: PERFORMANCE_CHART_EXPANDED_VALUE_DOT_PX,
            height: PERFORMANCE_CHART_EXPANDED_VALUE_DOT_PX,
          }}
        />
      </div>
      <span className="font-lufga text-[10px] leading-tight text-gray-400 text-center max-w-[72px]">
        {label}
      </span>
    </div>
  );
}

function ExpandedBar({ value, maxPositive, maxNegative, barWidth, hourIndex }) {
  const barHeight = EXPANDED_BAR_HEIGHT;
  const pad = getBarPadding(barWidth);
  const contentHeight = barHeight - pad.top - pad.bottom;
  const centerY = pad.top + contentHeight / 2;

  const maxHalfFill = contentHeight / 2 - EXPANDED_FILL_INSET;

  const isPositive = value >= 0;
  const fillRatio = isPositive
    ? Math.min(1, value / maxPositive)
    : Math.min(1, Math.abs(value) / maxNegative);
  const fillHeight = maxHalfFill * fillRatio;

  const innerBarWidth = barWidth - pad.left - pad.right;
  const fillWidth = Math.min(32, Math.max(16, innerBarWidth - 20));

  const dotPosition = isPositive ? centerY - fillHeight : centerY + fillHeight;

  const dotPositions = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div
      className="flex flex-col items-center shrink-0"
      style={{ width: barWidth }}
    >
      <div
        className="relative overflow-hidden"
        style={{
          width: barWidth,
          height: barHeight,
          paddingTop: pad.top,
          paddingRight: pad.right,
          paddingBottom: pad.bottom,
          paddingLeft: pad.left,
          border: "1.5px solid rgba(184, 184, 184, 1)",
          borderRadius: 100,
          backgroundColor: "#fff",
          boxSizing: "border-box",
        }}
      >
        {dotPositions.map((frac) => (
          <div
            key={frac}
            className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              left: "50%",
              top: `${pad.top + frac * contentHeight}px`,
              width: PERFORMANCE_CHART_SMALL_DOT_WIDTH_PX,
              height: PERFORMANCE_CHART_SMALL_DOT_HEIGHT_PX,
              borderRadius: 100,
              backgroundColor: "rgba(184, 184, 184, 1)",
            }}
          />
        ))}

        <div
          className="absolute left-1/2 -translate-x-1/2 "
          style={{
            width: fillWidth,
            height: fillHeight,

            backgroundColor: isPositive
              ? "rgba(237, 255, 140, 0.8)"
              : "rgba(255, 67, 67, 0.6)",
            ...(isPositive
              ? {
                  top: centerY - fillHeight,

                  borderBottomLeftRadius: 100,
                  borderBottomRightRadius: 100,
                }
              : {
                  top: centerY,
                  borderTopLeftRadius: 100,
                  borderTopRightRadius: 100,
                }),
          }}
        />

        <div
          className="absolute left-1/2 rounded-full bg-black -translate-x-1/2 -translate-y-1/2 z-10"
          style={{
            top: dotPosition,

            width: fillWidth,
            height: fillWidth,
            borderRadius: fillWidth,
          }}
        />
      </div>
      <span className="font-lufga font-medium text-[12px] leading-[100%] tracking-[0px] text-[#1B1B1B] text-center mt-2 w-[80px]">
        {formatExpandedHourStartIST(hourIndex)}
      </span>
    </div>
  );
}

//Code changes for the bar width end

export default function DoctorPerformanceReport() {
  const [caseType, setCaseType] = useState("All");
  const [caseStage, setCaseStage] = useState("All");
  const [timeframe, setTimeframe] = useState("Monthly");
  const [doctorLabel, setDoctorLabel] = useState("All Doctors");
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [ccDoctorList, setCcDoctorList] = useState([]);
  const [ccDoctorTotalPages, setCcDoctorTotalPages] = useState(1);
  const [ccDoctorLastPage, setCcDoctorLastPage] = useState(0);
  const [ccDoctorLoading, setCcDoctorLoading] = useState(false);
  const [ccDoctorLoadingMore, setCcDoctorLoadingMore] = useState(false);
  const ccDoctorLoadingMoreRef = useRef(false);
  const [ccDoctorSearchKey, setCcDoctorSearchKey] = useState("");
  /** -1 = no row; 0… = index in "All Doctors" (if shown) + doctor rows. */
  const [ccDoctorKeyboardIndex, setCcDoctorKeyboardIndex] = useState(-1);
  const ccDoctorKeyboardIndexRef = useRef(-1);
  ccDoctorKeyboardIndexRef.current = ccDoctorKeyboardIndex;
  const [timeframeOpen, setTimeframeOpen] = useState(false);
  /** Keyboard highlight index in timeframe dropdown (-1 = none). */
  const [timeframeKeyboardIndex, setTimeframeKeyboardIndex] = useState(-1);
  const timeframeKeyboardIndexRef = useRef(-1);
  timeframeKeyboardIndexRef.current = timeframeKeyboardIndex;
  const [doctorOpen, setDoctorOpen] = useState(false);
  /** Chart range: preset from `timeframe` dropdown, or custom from Datewise popover. */
  const [chartDateSource, setChartDateSource] = useState("preset");
  const [customRangeStart, setCustomRangeStart] = useState("");
  const [customRangeEnd, setCustomRangeEnd] = useState("");
  const [datewiseOpen, setDatewiseOpen] = useState(false);
  const [datewiseDraftStart, setDatewiseDraftStart] = useState("");
  const [datewiseDraftEnd, setDatewiseDraftEnd] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("Report");
  const [doctorStatsPage1Body, setDoctorStatsPage1Body] = useState(null);
  const [doctorStatsPrefetchDone, setDoctorStatsPrefetchDone] = useState(false);
  const [caseStageProcessReport, setCaseStageProcessReport] = useState(null);
  const [caseStageProcessLoading, setCaseStageProcessLoading] = useState(false);
  /** From GET cc-user-stage-time-action (once per page visit); maps performedTask.commandCenterStage → Action label. */
  const [commandCenterStageAction, setCommandCenterStageAction] =
    useState(null);
  /** Nested maps: ccStageTime[performedTaskId | "null"][commandCenterStage] → hours string to add to performedTask.createdAt for Due. */
  const [ccStageTime, setCcStageTime] = useState(null);

  /** GET cc-doctor-processed-stage-count — header radio counts (debounced search to match report grid). */
  const [debouncedSearchForCounts, setDebouncedSearchForCounts] = useState("");
  const [headerStageCounts, setHeaderStageCounts] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingRefreshCalls, setPendingRefreshCalls] = useState(0);
  const [headerStageCountsLoading, setHeaderStageCountsLoading] =
    useState(true);
  /** Increment to re-run data fetches without full page reload (refresh control). */
  const [dataRefreshNonce, setDataRefreshNonce] = useState(0);
  const refreshReportData = useCallback(() => {
    setIsRefreshing(true);
    setPendingRefreshCalls(4); // number of APIs triggered on refresh
    setDataRefreshNonce((n) => n + 1);
  }, []);

  const markRefreshApiComplete = useCallback(() => {
    setPendingRefreshCalls((prev) => {
      const next = prev - 1;
      if (next <= 0) {
        setIsRefreshing(false);
        return 0;
      }
      return next;
    });
  }, []);

  const timeframeOptions = [
    { value: "All Monthly", label: "All Monthly" },
    { value: "Today", label: "Today" },
    { value: "Weekly", label: "Weekly" },
    { value: "Monthly", label: "Monthly" },
    { value: "Till Now", label: "Till Now" },
    { value: "All Today", label: "All Today" },
    { value: "All Weekly", label: "All Weekly" },
  ];
  const [isMediumScreen, setIsMediumScreen] = useState(false);
  useEffect(() => {
    const check = () => setIsMediumScreen(window.innerWidth < 1536);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const q = String(searchQuery).trim();
    const delay = q === "" ? 0 : 350;
    const t = setTimeout(() => setDebouncedSearchForCounts(q), delay);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;
    setHeaderStageCountsLoading(true);
    const { startDate: rawStartDate, endDate: rawEndDate } =
      chartDateSource === "custom" && customRangeStart && customRangeEnd
        ? getChartDateRangeForYmdStrings(customRangeStart, customRangeEnd)
        : getChartDateRangeForTimeframe(timeframe);

    const startDate = rawStartDate?.split("+")[0];
    const endDate = rawEndDate?.split("+")[0];

    const params = {
      ...buildCcDoctorProcessedStageCountParams({
        caseSearchKey: debouncedSearchForCounts,
        doctorId: selectedDoctorId,
      }),
      startDate,
      endDate,
    };
    ReportServices.getCcDoctorProcessedStageCount(params)
      .then((res) => {
        if (cancelled) return;
        setHeaderStageCounts(
          mapProcessedStageCounts(
            normalizeProcessedStageCountPayload(res?.data),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setHeaderStageCounts(null);
      })
      .finally(() => {
        if (!cancelled) setHeaderStageCountsLoading(false);
        markRefreshApiComplete();
      });
    return () => {
      cancelled = true;
    };
  }, [
    selectedDoctorId,
    debouncedSearchForCounts,
    timeframe,
    chartDateSource,
    customRangeStart,
    customRangeEnd,
    dataRefreshNonce,
  ]);

  const DROPDOWN_BTN_CLASS =
    "flex items-center justify-center rounded-full font-lufga font-normal text-[12px] leading-[12px] tracking-0 text-black cursor-pointer outline-none transition-colors hover:bg-gray-50/90 shrink-0";
  const isLaptopScreen = window.innerWidth >= 1280 && window.innerWidth <= 1580;

  const DROPDOWN_STYLE = {
    width: isLaptopScreen ? 160 : 193,
    height: isLaptopScreen ? 42 : 53,
    paddingTop: isLaptopScreen ? 6 : 20,
    paddingBottom: isLaptopScreen ? 6 : 20,
    paddingLeft: isLaptopScreen ? 10 : 20,
    paddingRight: isLaptopScreen ? 10 : 20,
    gap: 10,
    borderRadius: isLaptopScreen ? 35 : 100,
    border: "1px solid rgba(1, 124, 113, 1)",
    background: "#FFFFFF",
  };
  /** Figma: open panel — white, teal border, large radius; options are plain centered text (no pill rows). */
  const DD_PANEL_CLASS =
    "absolute z-50 mt-0 flex flex-col min-w-[220px] max-w-[min(92vw,320px)] max-h-[min(60vh,380px)] rounded-[40px] border border-solid border-[rgba(1,124,113,1)] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.08)] overflow-hidden";
  /** Timeframe menu — width 200, radius 35, padding 16/10, gap 10; no scroll; solid white panel. Flush under trigger (no mt). */
  const DD_TIMEFRAME_PANEL_CLASS =
    "absolute z-50 mt-0 left-0 top-full box-border flex w-[200px] h-auto flex-col overflow-hidden rounded-[35px] border border-solid border-[rgba(1,124,113,1)] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.08)]";
  const DD_TIMEFRAME_LIST_CLASS =
    "flex flex-col gap-[10px] overflow-hidden px-[10px] py-4";
  /** Flat list: no per-row border/box — same white as panel (reset native button chrome). */
  const DD_TIMEFRAME_OPTION_CLASS =
    "m-0 flex w-full shrink-0 cursor-pointer appearance-none items-center justify-center gap-1.5 border-0 bg-transparent px-1 font-lufga font-normal text-[12px] leading-[14px] text-black shadow-none outline-none ring-0 transition-colors hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 active:bg-transparent";
  const DD_PANEL_HEADER_CLASS =
    "flex shrink-0 items-center justify-center gap-2.5 px-6 pt-6 pb-3 font-lufga font-normal text-sm text-black text-center";
  /** Doctor list rows — tight vertical rhythm so names read as one continuous list. */
  const DD_OPTION_BASE =
    "m-0 w-full cursor-pointer appearance-none border-0 bg-transparent py-1.5 px-3 text-center font-lufga font-normal text-sm leading-snug text-black shadow-none outline-none ring-0 transition-colors hover:bg-gray-50/80 focus-visible:ring-2 focus-visible:ring-[rgba(1,124,113,0.25)] focus-visible:ring-offset-0 active:bg-transparent";
  /** Keyboard (arrow) highlight — visible row so users see which doctor is focused. */
  const DD_OPTION_ACTIVE =
    " font-medium text-[rgba(1,124,113,1)] bg-[rgba(1,124,113,0.12)] ring-1 ring-inset ring-[rgba(1,124,113,0.22)] rounded-none";
  const DD_TIMEFRAME_OPTION_ACTIVE =
    " font-medium text-[rgba(1,124,113,1)] bg-[rgba(1,124,113,0.08)] rounded-lg";

  const timeframeRef = useRef(null);
  const doctorRef = useRef(null);
  const datewiseRef = useRef(null);
  const ccDoctorSearchInputRef = useRef(null);
  /** Scroll target: report / statistics table below the charts. */
  const reportSectionRef = useRef(null);
  /** Cleared on Enter so idle scroll does not run after user already jumped to the table. */
  const reportSearchScrollIdleTimerRef = useRef(null);
  const keyboardNavRef = useRef({
    doctorNavList: [],
    showAllDoctorsRow: false,
  });

  const scrollToReportSection = useCallback(() => {
    window.requestAnimationFrame(() => {
      reportSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  /** Scroll to report table only after user pauses typing (~2s) or presses Enter — not while they type. */
  useEffect(() => {
    if (reportSearchScrollIdleTimerRef.current) {
      clearTimeout(reportSearchScrollIdleTimerRef.current);
      reportSearchScrollIdleTimerRef.current = null;
    }
    const q = String(searchQuery).trim();
    if (q === "") return undefined;
    reportSearchScrollIdleTimerRef.current = window.setTimeout(() => {
      reportSearchScrollIdleTimerRef.current = null;
      scrollToReportSection();
    }, CASE_SEARCH_SCROLL_IDLE_MS);
    return () => {
      if (reportSearchScrollIdleTimerRef.current) {
        clearTimeout(reportSearchScrollIdleTimerRef.current);
        reportSearchScrollIdleTimerRef.current = null;
      }
    };
  }, [searchQuery, scrollToReportSection]);

  const doctorNavList = useMemo(
    () => ccDoctorList.filter((u) => u?.id && u.id !== selectedDoctorId),
    [ccDoctorList, selectedDoctorId],
  );
  const showAllDoctorsRow = selectedDoctorId != null;
  const doctorNavCount = (showAllDoctorsRow ? 1 : 0) + doctorNavList.length;

  keyboardNavRef.current = {
    doctorNavList,
    showAllDoctorsRow,
  };

  /** Arrow/Enter/Home/End for doctor list — invoked from document capture when focus is inside the doctor dropdown. */
  const handleDoctorListKeyNavigation = useCallback((e) => {
    const { doctorNavList: list, showAllDoctorsRow: showAll } =
      keyboardNavRef.current;
    const n = (showAll ? 1 : 0) + list.length;
    switch (e.key) {
      case "ArrowDown": {
        if (n === 0) return;
        e.preventDefault();
        setCcDoctorKeyboardIndex((prev) => {
          if (prev < 0) return 0;
          return Math.min(prev + 1, n - 1);
        });
        break;
      }
      case "ArrowUp": {
        if (n === 0) return;
        e.preventDefault();
        setCcDoctorKeyboardIndex((prev) => {
          if (prev <= 0) {
            queueMicrotask(() => ccDoctorSearchInputRef.current?.focus());
            return -1;
          }
          return prev - 1;
        });
        break;
      }
      case "Enter": {
        const idx = ccDoctorKeyboardIndexRef.current;
        if (idx < 0 || idx >= n) return;
        e.preventDefault();
        if (showAll && idx === 0) {
          setSelectedDoctorId(null);
          setDoctorLabel("All Doctors");
          setDoctorOpen(false);
          return;
        }
        const di = showAll ? idx - 1 : idx;
        const u = list[di];
        if (!u?.id) return;
        setSelectedDoctorId(String(u.id));
        setDoctorLabel(ccDoctorDisplayName(u));
        setDoctorOpen(false);
        break;
      }
      case "Home": {
        if (n === 0) return;
        e.preventDefault();
        setCcDoctorKeyboardIndex(0);
        break;
      }
      case "End": {
        if (n === 0) return;
        e.preventDefault();
        setCcDoctorKeyboardIndex(n - 1);
        break;
      }
      default:
        break;
    }
  }, []);

  const loadCcDoctors = useCallback(
    async (page, append) => {
      if (append) {
        if (ccDoctorLoadingMoreRef.current) return;
        ccDoctorLoadingMoreRef.current = true;
        setCcDoctorLoadingMore(true);
      } else {
        setCcDoctorLoading(true);
      }
      try {
        const res = await InterUserMangementServices.getCcDoctorTeamUsers({
          limit: CC_DOCTOR_PAGE_LIMIT,
          page,
          searchKey: ccDoctorSearchKey.trim(),
        });
        const data = res?.data ?? {};
        const teamUsers = Array.isArray(data.teamUsers) ? data.teamUsers : [];
        const tp = Math.max(1, Number(data.totalPages) || 1);
        const cur = Number(
          data.curentPage != null
            ? data.curentPage
            : data.currentPage != null
              ? data.currentPage
              : page,
        );
        setCcDoctorTotalPages(tp);
        setCcDoctorLastPage(cur);
        setCcDoctorList((prev) =>
          append
            ? mergeTeamUsersById(prev, teamUsers)
            : mergeTeamUsersById([], teamUsers),
        );
      } catch {
        if (!append) {
          setCcDoctorList([]);
          setCcDoctorKeyboardIndex(-1);
        }
      } finally {
        setCcDoctorLoading(false);
        setCcDoctorLoadingMore(false);
        ccDoctorLoadingMoreRef.current = false;
      }
    },
    [ccDoctorSearchKey],
  );

  useEffect(() => {
    if (!doctorOpen) return;
    const debounceMs = ccDoctorSearchKey.trim() ? 350 : 0;
    const timer = setTimeout(() => {
      loadCcDoctors(1, false);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [doctorOpen, ccDoctorSearchKey, loadCcDoctors]);

  useEffect(() => {
    if (!doctorOpen) return;
    if (dataRefreshNonce === 0) return;
    loadCcDoctors(1, false);
  }, [dataRefreshNonce, doctorOpen, loadCcDoctors]);

  const handleDoctorListScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollTop + clientHeight < scrollHeight - 48) return;
      if (
        ccDoctorLoading ||
        ccDoctorLoadingMore ||
        ccDoctorLoadingMoreRef.current
      )
        return;
      if (ccDoctorLastPage >= ccDoctorTotalPages) return;
      loadCcDoctors(ccDoctorLastPage + 1, true);
    },
    [
      ccDoctorLoading,
      ccDoctorLoadingMore,
      ccDoctorLastPage,
      ccDoctorTotalPages,
      loadCcDoctors,
    ],
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (timeframeRef.current && !timeframeRef.current.contains(e.target))
        setTimeframeOpen(false);
      if (doctorRef.current && !doctorRef.current.contains(e.target))
        setDoctorOpen(false);
      if (datewiseRef.current && !datewiseRef.current.contains(e.target))
        setDatewiseOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!datewiseOpen) return;
    if (chartDateSource === "custom" && customRangeStart && customRangeEnd) {
      setDatewiseDraftStart(customRangeStart);
      setDatewiseDraftEnd(customRangeEnd);
    } else {
      const { startDate, endDate } = getChartDateRangeForTimeframe(timeframe);
      setDatewiseDraftStart(localYmdFromOffsetISO(startDate));
      setDatewiseDraftEnd(localYmdFromOffsetISO(endDate));
    }
  }, [
    datewiseOpen,
    chartDateSource,
    customRangeStart,
    customRangeEnd,
    timeframe,
  ]);

  useEffect(() => {
    if (!doctorOpen) {
      setCcDoctorSearchKey("");
      setCcDoctorKeyboardIndex(-1);
    }
  }, [doctorOpen]);

  useEffect(() => {
    if (!timeframeOpen) setTimeframeKeyboardIndex(-1);
  }, [timeframeOpen]);

  /** Move focus into the doctor dropdown so Arrow keys work (global search can keep focus otherwise). */
  useEffect(() => {
    if (!doctorOpen) return;
    const id = window.setTimeout(() => {
      ccDoctorSearchInputRef.current?.focus({ preventScroll: true });
    }, 0);
    return () => clearTimeout(id);
  }, [doctorOpen]);

  useEffect(() => {
    if (!doctorOpen) return;
    setCcDoctorKeyboardIndex((prev) => {
      if (prev < 0) return prev;
      if (doctorNavCount === 0) return -1;
      return Math.min(prev, doctorNavCount - 1);
    });
  }, [doctorOpen, doctorNavCount]);

  useEffect(() => {
    if (!doctorOpen || ccDoctorKeyboardIndex < 0) return;
    document
      .getElementById(`cc-doctor-dd-opt-${ccDoctorKeyboardIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [doctorOpen, ccDoctorKeyboardIndex]);

  useEffect(() => {
    if (!doctorOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setDoctorOpen(false);
        return;
      }

      const root = doctorRef.current;
      if (!root) return;
      const target = e.target;
      const focusInside = target instanceof Node && root.contains(target);

      const { doctorNavList: list, showAllDoctorsRow: showAll } =
        keyboardNavRef.current;
      const n = (showAll ? 1 : 0) + list.length;

      /** If focus is outside the doctor control (e.g. global search) but the list is open, route arrows into the list. */
      if (!focusInside) {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          if (n === 0) return;
          e.preventDefault();
          ccDoctorSearchInputRef.current?.focus({ preventScroll: true });
          requestAnimationFrame(() => {
            setCcDoctorKeyboardIndex(e.key === "ArrowDown" ? 0 : n - 1);
          });
        }
        return;
      }

      /** Single path for search field + list option buttons (native capture runs before React; no early-return for input). */
      handleDoctorListKeyNavigation(e);
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [doctorOpen, handleDoctorListKeyNavigation]);

  useEffect(() => {
    if (!timeframeOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setTimeframeOpen(false);
        return;
      }
      const root = timeframeRef.current;
      if (!root || !(e.target instanceof Node) || !root.contains(e.target)) {
        return;
      }
      const n = timeframeOptions.length;
      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          setTimeframeKeyboardIndex((prev) => {
            if (prev < 0) return 0;
            return Math.min(prev + 1, n - 1);
          });
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          setTimeframeKeyboardIndex((prev) => {
            if (prev <= 0) return n - 1;
            return prev - 1;
          });
          break;
        }
        case "Enter": {
          const idx = timeframeKeyboardIndexRef.current;
          if (idx < 0 || idx >= n) return;
          e.preventDefault();
          const opt = timeframeOptions[idx];
          if (!opt) return;
          setTimeframe(opt.label);
          setChartDateSource("preset");
          setTimeframeOpen(false);
          break;
        }
        case "Home": {
          e.preventDefault();
          setTimeframeKeyboardIndex(0);
          break;
        }
        case "End": {
          e.preventDefault();
          setTimeframeKeyboardIndex(n - 1);
          break;
        }
        default:
          break;
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [timeframeOpen]);

  useEffect(() => {
    if (!timeframeOpen || timeframeKeyboardIndex < 0) return;
    document
      .getElementById(`cc-timeframe-opt-${timeframeKeyboardIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [timeframeOpen, timeframeKeyboardIndex]);

  /** Prefetch statistics page 1 — same `doctorId` as chart when a doctor is selected (unfiltered when All Doctors). */
  useEffect(() => {
    let cancelled = false;
    setDoctorStatsPrefetchDone(false);
    setDoctorStatsPage1Body(null);
    const did =
      selectedDoctorId != null && String(selectedDoctorId).trim() !== ""
        ? String(selectedDoctorId).trim()
        : undefined;
    ReportServices.getCcDoctorCaseCountReport({
      limit: DOCTOR_STATS_PAGE_LIMIT,
      page: 1,
      ...(did ? { doctorId: did } : {}),
    })
      .then((res) => {
        if (!cancelled) setDoctorStatsPage1Body(res?.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setDoctorStatsPage1Body(null);
      })
      .finally(() => {
        if (!cancelled) setDoctorStatsPrefetchDone(true);
        markRefreshApiComplete();
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDoctorId, dataRefreshNonce]);

  /** GET cc-user-stage-time-action — on mount and when data is refreshed. */
  useEffect(() => {
    let cancelled = false;
    ReportServices.getCcUserStageTimeAction()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? {};
        const act = data.commandCenterStageAction;
        setCommandCenterStageAction(
          act != null && typeof act === "object" ? act : null,
        );
        const cst = data.ccStageTime;
        setCcStageTime(cst != null && typeof cst === "object" ? cst : null);
      })
      .catch(() => {
        if (!cancelled) {
          setCommandCenterStageAction(null);
          setCcStageTime(null);
        }
      })
      .finally(() => {
        markRefreshApiComplete();
      });
    return () => {
      cancelled = true;
    };
  }, [dataRefreshNonce]);

  /** Performance chart: GET cc-case-stage-process-report — doctorId from team user; dates from timeframe filter. */
  useEffect(() => {
    // if (selectedDoctorId == null || String(selectedDoctorId).trim() === "") {
    //   setCaseStageProcessReport(null);
    //   setCaseStageProcessLoading(false);
    //   return;
    // }
    const { startDate: rawStartDate, endDate: rawEndDate } =
      chartDateSource === "custom" &&
      customRangeStart &&
      String(customRangeStart).trim() !== "" &&
      customRangeEnd &&
      String(customRangeEnd).trim() !== ""
        ? getChartDateRangeForYmdStrings(customRangeStart, customRangeEnd)
        : getChartDateRangeForTimeframe(timeframe);

    // remove timezone offset like +05:30
    const startDate = rawStartDate?.split("+")[0];
    const endDate = rawEndDate?.split("+")[0];
    let cancelled = false;
    setCaseStageProcessLoading(true);
    const payload = selectedDoctorId
      ? {
          doctorId: String(selectedDoctorId),
          startDate,
          endDate,
        }
      : {
          startDate,
          endDate,
        };
    ReportServices.getCcCaseStageProcessReport(payload)
      .then((res) => {
        if (!cancelled) setCaseStageProcessReport(res?.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setCaseStageProcessReport(null);
      })
      .finally(() => {
        if (!cancelled) setCaseStageProcessLoading(false);
        markRefreshApiComplete();
      });
    return () => {
      cancelled = true;
    };
  }, [
    selectedDoctorId,
    timeframe,
    chartDateSource,
    customRangeStart,
    customRangeEnd,
    dataRefreshNonce,
  ]);

  const applyDatewiseRange = useCallback(() => {
    const s = String(datewiseDraftStart || "").trim();
    const e = String(datewiseDraftEnd || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || !/^\d{4}-\d{2}-\d{2}$/.test(e))
      return;
    setChartDateSource("custom");
    setCustomRangeStart(s);
    setCustomRangeEnd(e);
    setDatewiseOpen(false);
  }, [datewiseDraftStart, datewiseDraftEnd]);

  const caseTypeOptions = [
    { value: "All", label: "All" },
    { value: "Daycare", label: "Daycare" },
    { value: "Conservative", label: "Conservative" },
    { value: "Surgical", label: "Surgical" },
  ];

  const caseStageOptions = [
    { value: "All", label: "All" },
    { value: "Admission", label: "Admission" },
    { value: "Enhancement", label: "Enhancement" },
    { value: "Claim", label: "Claim" },
  ];

  const timeframeDisplayLabel =
    chartDateSource === "custom" && customRangeStart && customRangeEnd
      ? formatYmdRangeLabel(customRangeStart, customRangeEnd)
      : timeframe;

  return (
    <div
      className="min-h-screen w-full flex-1 min-w-0 bg-[#f5f5f5] p-4 md:p-5 lg:p-6 xl:p-6 2xl:p-8 font-lufga overflow-x-hidden box-border"
      style={{ marginTop: 100 }}
    >
      {/* Header: breadcrumb, then [Timeframe][Doctor][Search] in one row (Figma) */}
      <div className="flex flex-col gap-3 lg:gap-4 mb-4 lg:mb-6">
        <span className="path_line font-lufga w-full text-sm font-semibold text-[#797979] flex items-center">
          <WidgetsOutlinedIcon style={{ height: "24px" }} /> &nbsp;/ &nbsp;
          Dashboard &nbsp;/ &nbsp; Doctor Performance Report
        </span>

        <div className="flex w-full min-w-0 flex-col gap-3 xl:flex-row xl:flex-wrap xl:items-center xl:justify-end xl:gap-2 xl:gap-3">
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 sm:gap-3 xl:shrink-0">
            <div className="relative shrink-0" ref={timeframeRef}>
              <button
                type="button"
                onClick={() => {
                  setTimeframeOpen(!timeframeOpen);
                  setDoctorOpen(false);
                  setDatewiseOpen(false);
                }}
                className={DROPDOWN_BTN_CLASS}
                style={DROPDOWN_STYLE}
                title={timeframeDisplayLabel}
              >
                <span className="truncate max-w-[140px]">
                  {timeframeDisplayLabel}
                </span>
                <img
                  src={ChevronDown}
                  alt=""
                  className="w-4 h-4 object-contain shrink-0"
                />
              </button>
              {timeframeOpen && (
                <div className={DD_TIMEFRAME_PANEL_CLASS} role="presentation">
                  <div
                    className={DD_TIMEFRAME_LIST_CLASS}
                    role="listbox"
                    aria-label="Time range"
                  >
                    {timeframeOptions.map((opt, i) => {
                      const isSelected =
                        chartDateSource === "preset" && opt.label === timeframe;
                      const isKb = timeframeKeyboardIndex === i;
                      return (
                        <button
                          key={opt.value}
                          id={`cc-timeframe-opt-${i}`}
                          type="button"
                          role="option"
                          aria-selected={isKb || isSelected}
                          onClick={() => {
                            setTimeframe(opt.label);
                            setChartDateSource("preset");
                            setTimeframeOpen(false);
                          }}
                          className={`${DD_TIMEFRAME_OPTION_CLASS}${
                            isKb ? DD_TIMEFRAME_OPTION_ACTIVE : ""
                          }`}
                        >
                          <span className="truncate text-center">
                            {opt.label}
                          </span>
                          {isSelected ? (
                            <img
                              src={ChevronDown}
                              alt=""
                              className="h-3 w-3 shrink-0 object-contain"
                            />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="relative shrink-0" ref={doctorRef}>
              <div
                className={`${DROPDOWN_BTN_CLASS} ${selectedDoctorId != null ? "gap-1" : ""}`}
                style={{
                  ...DROPDOWN_STYLE,
                  ...(selectedDoctorId != null
                    ? {
                        width: (isMediumScreen ? 170 : 193) + 22,
                      }
                    : {}),
                }}
                role="group"
                aria-label="Doctor filter"
              >
                <button
                  type="button"
                  onClick={() => {
                    setDoctorOpen(!doctorOpen);
                    setTimeframeOpen(false);
                    setDatewiseOpen(false);
                  }}
                  className="flex min-w-0 flex-1 items-center justify-center gap-2.5 border-0 bg-transparent p-0 font-inherit text-inherit cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[rgba(1,124,113,0.35)] focus-visible:ring-offset-0 rounded-full"
                >
                  <span
                    className={`truncate ${selectedDoctorId != null ? "max-w-[min(100px,28vw)]" : "max-w-[140px]"}`}
                  >
                    {doctorLabel}
                  </span>
                  <img
                    src={ChevronDown}
                    alt=""
                    className="w-4 h-4 object-contain shrink-0"
                  />
                </button>
                {selectedDoctorId != null && (
                  <button
                    type="button"
                    aria-label="Clear doctor selection"
                    className="flex shrink-0 items-center justify-center border-0 bg-transparent p-0 pr-0.5 text-gray-500 hover:text-gray-800 rounded-none outline-none focus-visible:outline-none focus-visible:text-gray-900"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDoctorId(null);
                      setDoctorLabel("All Doctors");
                      setDoctorOpen(false);
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </button>
                )}
              </div>
              {doctorOpen && (
                <div
                  className={`${DD_PANEL_CLASS} right-0 top-full left-auto`}
                  role="presentation"
                >
                  <div className={DD_PANEL_HEADER_CLASS}>
                    <span className="truncate max-w-[220px]">
                      {doctorLabel}
                    </span>
                    <img
                      src={ChevronDown}
                      alt=""
                      className="w-4 h-4 shrink-0 object-contain"
                    />
                  </div>
                  <div className="shrink-0 px-5 pb-3">
                    <input
                      id="cc-doctor-search-input"
                      ref={ccDoctorSearchInputRef}
                      type="text"
                      inputMode="search"
                      placeholder="Search doctor"
                      value={ccDoctorSearchKey}
                      onChange={(e) => setCcDoctorSearchKey(e.target.value)}
                      className="w-full rounded-full border border-solid border-[rgba(1,124,113,0.35)] bg-[#F5F5F5] px-3 py-2.5 text-sm font-lufga text-gray-900 placeholder:text-gray-500 outline-none focus:border-[rgba(1,124,113,1)] focus:ring-1 focus:ring-[rgba(1,124,113,0.35)]"
                      autoComplete="off"
                      aria-label="Search doctors by name"
                      aria-controls="cc-doctor-dd-listbox"
                      aria-activedescendant={
                        ccDoctorKeyboardIndex >= 0
                          ? `cc-doctor-dd-opt-${ccDoctorKeyboardIndex}`
                          : undefined
                      }
                    />
                  </div>
                  <div
                    id="cc-doctor-dd-listbox"
                    role="listbox"
                    aria-label="Doctors"
                    onScroll={handleDoctorListScroll}
                    className="flex min-h-0 flex-1 flex-col gap-0 divide-y divide-gray-200/80 overflow-y-auto overflow-x-hidden rounded-b-2xl px-0 pb-4 [scrollbar-gutter:stable] bg-[rgba(255,255,255,0.6)]"
                  >
                    {selectedDoctorId != null && (
                      <button
                        id="cc-doctor-dd-opt-0"
                        type="button"
                        role="option"
                        aria-selected={ccDoctorKeyboardIndex === 0}
                        onClick={() => {
                          setSelectedDoctorId(null);
                          setDoctorLabel("All Doctors");
                          setDoctorOpen(false);
                        }}
                        className={`${DD_OPTION_BASE}${
                          ccDoctorKeyboardIndex === 0 ? DD_OPTION_ACTIVE : ""
                        }`}
                      >
                        All Doctors
                      </button>
                    )}
                    {ccDoctorLoading && ccDoctorList.length === 0 && (
                      <div className="px-3 py-1.5 text-sm text-gray-500 font-lufga text-center leading-snug">
                        Loading doctors…
                      </div>
                    )}
                    {doctorNavList.map((u, i) => {
                      const label = ccDoctorDisplayName(u);
                      const navIndex = showAllDoctorsRow ? i + 1 : i;
                      return (
                        <button
                          key={u.id}
                          id={`cc-doctor-dd-opt-${navIndex}`}
                          type="button"
                          role="option"
                          aria-selected={ccDoctorKeyboardIndex === navIndex}
                          onClick={() => {
                            setSelectedDoctorId(
                              u.id != null ? String(u.id) : null,
                            );
                            setDoctorLabel(label);
                            setDoctorOpen(false);
                          }}
                          className={`${DD_OPTION_BASE}${
                            ccDoctorKeyboardIndex === navIndex
                              ? DD_OPTION_ACTIVE
                              : ""
                          }`}
                        >
                          <span className="block text-center break-words">
                            {label}
                          </span>
                        </button>
                      );
                    })}
                    {ccDoctorLoadingMore && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 font-lufga text-center leading-snug">
                        Loading more…
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="w-full min-w-0 xl:flex-1 xl:max-w-[480px] 2xl:max-w-[634px] xl:min-w-[min(100%,200px)]">
            <div className="flex h-[42px] xl:h-[53px] min-h-[42px] xl:min-h-[48px] w-full xl:max-w-[634px] max-w-[480px] min-w-0 items-center box-border rounded-[52px] border border-solid border-white bg-[#FFFFFF99] py-[15px] px-[16px] xl:py-[15px] xl:pl-[21px] xl:pr-[21px] gap-[10px]">
              {" "}
              <input
                type="text"
                enterKeyHint="search"
                placeholder="Search by Patient name, Claim ID, Hospital name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  if (String(searchQuery).trim() === "") return;
                  if (reportSearchScrollIdleTimerRef.current) {
                    clearTimeout(reportSearchScrollIdleTimerRef.current);
                    reportSearchScrollIdleTimerRef.current = null;
                  }
                  scrollToReportSection();
                }}
                className="flex-1 min-w-0 min-h-0 bg-transparent border-none outline-none font-lufga font-normal text-[14px] xl:text-base leading-[100%] xl:leading-none tracking-[-0.05em] text-gray-900 placeholder:font-lufga placeholder:font-normal placeholder:text-[14px] xl:placeholder:text-base placeholder:leading-[100%] xl:placeholder:leading-none placeholder:tracking-[-0.05em] placeholder:text-[#797979] focus:ring-0"
              />
              <img
                src={searchNewIcon}
                alt="Search"
                className="flex-shrink-0"
                style={{ width: 23, height: 23, opacity: 1 }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bars - Figma: bg #FFFFFF99, radio 16x16, 2px black border, 7.5px radius */}
      <div className="flex flex-nowrap items-center justify-between gap-3 2xl:gap-6 mb-4 lg:mb-6">
        {/* Case Type Filter */}
        <div className="flex min-w-0 flex-wrap items-center gap-[10px] px-[8px] py-[12px] rounded-[24px] bg-[#FFFFFF99] 2xl:gap-4 2xl:px-5 2xl:py-2.5 2xl:rounded-full">
          {caseTypeOptions.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-[2px] cursor-pointer"
            >
              <input
                type="radio"
                name="caseType"
                value={opt.value}
                checked={caseType === opt.value}
                onChange={() => setCaseType(opt.value)}
                className="sr-only"
              />

              {/* Radio */}
              <span
                className="inline-flex items-center justify-center flex-shrink-0"
                style={{
                  width: 16,
                  height: 16,
                  border: "2px solid #000000",
                  borderRadius: 7.5,
                  backgroundColor: "transparent",
                }}
              >
                {caseType === opt.value && (
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 7.5,
                      backgroundColor: "#000000",
                    }}
                  />
                )}
              </span>

              {/* Label */}
              <span className="font-lufga font-normal text-[12px] leading-[14px] tracking-[-0.03em] text-gray-900 2xl:text-sm 2xl:font-medium 2xl:leading-normal">
                {opt.label ?? opt.value}

                {!headerStageCountsLoading &&
                  headerStageCounts &&
                  Number.isFinite(headerStageCounts.caseType[opt.value]) && (
                    <span className="ml-[2px] tabular-nums font-lufga font-normal text-[12px] leading-[14px] tracking-[-0.03em] text-center 2xl:text-sm 2xl:leading-normal">
                      {formatHeaderFilterCount(
                        headerStageCounts.caseType[opt.value],
                      )}
                    </span>
                  )}
              </span>
            </label>
          ))}
        </div>

        {/* Case Stage Filter */}
        <div className="flex min-w-0 flex-wrap items-center gap-[10px] px-[8px] py-[12px] rounded-[24px] bg-[#FFFFFF99] 2xl:gap-4 2xl:px-5 2xl:py-2.5 2xl:rounded-full">
          {caseStageOptions.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-[2px] cursor-pointer"
            >
              <input
                type="radio"
                name="caseStage"
                value={opt.value}
                checked={caseStage === opt.value}
                onChange={() => setCaseStage(opt.value)}
                className="sr-only"
              />

              {/* Radio */}
              <span
                className="inline-flex items-center justify-center flex-shrink-0"
                style={{
                  width: 16,
                  height: 16,
                  border: "2px solid #000000",
                  borderRadius: 7.5,
                  backgroundColor: "transparent",
                }}
              >
                {caseStage === opt.value && (
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 7.5,
                      backgroundColor: "#000000",
                    }}
                  />
                )}
              </span>

              {/* Label */}
              <span className="font-lufga font-normal text-[12px] leading-[14px] tracking-[-0.03em] text-gray-900 2xl:text-sm 2xl:font-medium 2xl:leading-normal">
                {opt.label ?? opt.value}

                {!headerStageCountsLoading &&
                  headerStageCounts &&
                  Number.isFinite(headerStageCounts.caseStage[opt.value]) && (
                    <span className="ml-[2px] tabular-nums font-lufga font-normal text-[12px] leading-[14px] tracking-[-0.03em] text-center 2xl:text-sm 2xl:leading-normal">
                      {formatHeaderFilterCount(
                        headerStageCounts.caseStage[opt.value],
                      )}
                    </span>
                  )}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Performance Card */}
      <div className="mb-4 lg:mb-6">
        {/* All Performance header - Figma: Lufga, 400, 36px, 100% line-height, -7% letter-spacing */}
        <div className="flex flex-col sm:flex-row flex-wrap justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 lg:mb-5">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              type="button"
              onClick={refreshReportData}
              aria-label="Refresh data"
              disabled={isRefreshing}
              className={`shrink-0 p-0.5 rounded-full border-0 bg-transparent transition-opacity outline-none focus-visible:ring-2 focus-visible:ring-[rgba(1,124,113,0.35)] focus-visible:ring-offset-0
  ${isRefreshing ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:opacity-80"}`}
            >
              {isRefreshing ? (
                <span className="text-sm text-gray-500">Refreshing...</span>
              ) : (
                <img
                  src={refreshIcon}
                  alt="refresh"
                  className="cursor-pointer"
                />
              )}
            </button>
            <h2 className="font-lufga font-normal text-2xl lg:text-[30px] xl:text-[36px] leading-[100%] tracking-[-0.07em] align-middle text-gray-900">
              All Performance
            </h2>
          </div>
          <div className="flex gap-4 xl:gap-[51.68px]">
            {viewMode === "Report" ? (
              <button
                type="button"
                onClick={() => {
                  setViewMode("Statistics");
                  scrollToReportSection();
                }}
                className="flex items-center justify-center gap-[10px] w-[140px] h-[47px] px-[18px] py-[17px] rounded-[26px] bg-white border-none cursor-pointer font-lufga font-medium text-[12px] leading-[12px] tracking-[-0.5px] capitalize text-gray-900 transition-colors hover:bg-gray-50"
              >
                <img
                  src={statisticsIcon}
                  alt=""
                  className="w-[14px] h-[14px] object-contain"
                />
                <span>Statistics</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setViewMode("Report");
                  scrollToReportSection();
                }}
                className="flex items-center justify-center gap-[10px] w-[140px] h-[47px] px-[18px] py-[17px] rounded-[26px] bg-white border-none cursor-pointer font-lufga font-medium text-[12px] leading-[12px] tracking-[-0.5px] capitalize text-gray-900 transition-colors hover:bg-gray-50"
              >
                <img
                  src={reportIcon}
                  alt=""
                  className="w-[14px] h-[14px] object-contain"
                />
                <span>Report</span>
              </button>
            )}
            <div className="relative" ref={datewiseRef}>
              <button
                type="button"
                onClick={() => {
                  setDatewiseOpen((o) => !o);
                  setTimeframeOpen(false);
                  setDoctorOpen(false);
                }}
                aria-expanded={datewiseOpen}
                aria-haspopup="dialog"
                className={`flex items-center justify-center gap-[10px] w-[140px] h-[47px] px-[18px] py-[17px] rounded-[26px] bg-white border-none cursor-pointer font-lufga font-medium text-[12px] leading-[12px] tracking-[-0.5px] capitalize text-gray-900 transition-colors hover:bg-gray-50 ${
                  chartDateSource === "custom"
                    ? "ring-2 ring-[rgba(1,124,113,0.45)]"
                    : ""
                }`}
              >
                <img
                  src={calenderIcon}
                  alt=""
                  className="w-[14px] h-[14px] object-contain"
                />
                <span className="font-lufga font-medium text-[12px] leading-[12px] tracking-[-0.5px] capitalize text-gray-900">
                  Datewise
                </span>
              </button>
              {datewiseOpen && (
                <div
                  className="absolute top-full right-0 mt-1 z-50 flex flex-col min-w-[min(92vw,280px)] rounded-[12px] bg-white border border-gray-200 shadow-lg p-4 gap-3"
                  role="dialog"
                  aria-label="Choose date range for charts"
                >
                  <p className="font-lufga text-sm font-medium text-gray-900">
                    Chart date range
                  </p>
                  <label className="flex flex-col gap-1">
                    <span className="font-lufga text-xs text-gray-600">
                      Start date
                    </span>
                    <input
                      type="date"
                      value={datewiseDraftStart}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => {
                        setDatewiseDraftStart(e.target.value);
                        setDatewiseDraftEnd("");
                      }}
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-lufga text-gray-900 outline-none focus:border-[#017C71] focus:ring-1 focus:ring-[#017C71]/35"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="font-lufga text-xs text-gray-600">
                      End date
                    </span>
                    <input
                      type="date"
                      value={datewiseDraftEnd}
                      min={datewiseDraftStart || undefined}
                      max={new Date().toISOString().split("T")[0]}
                      disabled={!datewiseDraftStart}
                      onChange={(e) => {
                        const value = e.target.value;
                        const today = new Date().toISOString().split("T")[0];

                        if (!value) return;

                        // block future dates
                        if (value > today) return;

                        // block dates before start date
                        if (datewiseDraftStart && value < datewiseDraftStart)
                          return;

                        setDatewiseDraftEnd(value);
                      }}
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-lufga text-gray-900 outline-none focus:border-[#017C71] focus:ring-1 focus:ring-[#017C71]/35"
                    />
                  </label>
                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setDatewiseOpen(false)}
                      className="rounded-full px-4 py-2 text-sm font-lufga text-gray-700 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={!datewiseDraftStart || !datewiseDraftEnd}
                      onClick={applyDatewiseRange}
                      className={`rounded-full px-4 py-2 text-sm font-lufga font-medium text-white bg-[#017C71] hover:bg-[#016a60] ${!datewiseDraftStart || !datewiseDraftEnd ? "cursor-not-allowed" : " cursor-pointer"}`}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            <DoctorReportDownloadButton
              selectedDoctorId={selectedDoctorId}
              timeframe={timeframe}
              chartDateSource={chartDateSource}
              customRangeStart={customRangeStart}
              customRangeEnd={customRangeEnd}
            />
          </div>
        </div>

        <PerformanceChart
          payload={caseStageProcessReport}
          loading={caseStageProcessLoading}
        />

        <div className="mt-[48px] flex flex-col gap-[48px]">
          <div className="w-full bg-white rounded-xl p-4 lg:p-6 border border-black min-h-[460px] flex items-center justify-center">
            <ExpandedChart payload={caseStageProcessReport} />
          </div>
          <div
            className="w-full shrink-0"
            style={{
              borderBottom: "1px solid rgb(255, 255, 255)",
            }}
          />
          <div
            ref={reportSectionRef}
            className="scroll-mt-24"
            id="doctor-performance-report-section"
          >
            {viewMode === "Statistics" ? (
              <StatisticsView
                key={
                  selectedDoctorId != null &&
                  String(selectedDoctorId).trim() !== ""
                    ? String(selectedDoctorId).trim()
                    : "__all_doctors__"
                }
                doctorId={selectedDoctorId}
                prefetchedPage1Body={doctorStatsPage1Body}
                prefetchPage1Ready={doctorStatsPrefetchDone}
                refreshNonce={dataRefreshNonce}
              />
            ) : (
              <ReportView
                commandCenterStageAction={commandCenterStageAction}
                ccStageTime={ccStageTime}
                caseTypeFilter={caseType}
                caseStageFilter={caseStage}
                caseSearchKey={searchQuery}
                doctorId={selectedDoctorId}
                refreshNonce={dataRefreshNonce}
                timeframe={timeframe}
                chartDateSource={chartDateSource}
                customRangeStart={customRangeStart}
                customRangeEnd={customRangeEnd}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
